// The global allow-list (`models.allowed_models`, ADR-103) as the Providers section and the provider
// modal see it (SCR-574, SCR-575): one catalog of the org's models by provider, the one rule that
// decides what each agent is offered, a row per provider, and what an edit in the modal changes,
// including which agents' defaults it would take away. Pure and DOM-free
// (tests/unit/models/provider-model.test.ts); the rule is the corpus's
// (contracts/fixtures/models/*.json) and the API's `OfferedModels`.
import type { components } from "../../api/schema";
import { getPath, isObject, setPath, type Json, type JsonObject } from "../../settings/inherit";
import { ALL_PROVIDERS, compareProviders, providerLabel } from "../../settings/pickers";
import { HARNESSES, itemDisplayName, policyOf, providersCover, type Harness, type HarnessPolicy, type InventoryItem, type ModelInventory } from "./model";

export type ProviderPolicy = components["schemas"]["InstallationSettingsV1ProviderPolicy"];
export type GlobalPolicy = readonly ProviderPolicy[];

export const ALLOWED_MODELS_PATH = "models.allowed_models";

// ------------------------------------------------------------------------------------ the catalog

/** One model of the org, once per family name. */
export interface CatalogModel {
  /** The family name a policy lists and a client sends (`claude-opus-5-5`); never shown as a label. */
  model: string;
  /** What a person reads ("Opus 5.5"). */
  displayName: string;
  provider: string;
  /** Some installation's account can call it. */
  invokable: boolean;
}

/**
 * The org's models from one or more inventories (an installation's, or every installation's at the
 * org default), once per family, invokable when any inventory can call it, in the inventories'
 * order. A model with no family name cannot be listed in a policy and is left out.
 */
export function catalogOf(inventories: readonly ModelInventory[]): CatalogModel[] {
  const out: CatalogModel[] = [];
  const index = new Map<string, number>();
  for (const inventory of inventories) {
    for (const group of inventory.providers) {
      for (const item of group.items) addItem(out, index, group.provider, item);
    }
  }
  return out;
}

function addItem(out: CatalogModel[], index: Map<string, number>, provider: string, item: InventoryItem): void {
  if (item.publicName === "") return;
  const invokable = item.invokability === "invokable";
  const seen = index.get(item.publicName);
  if (seen === undefined) {
    index.set(item.publicName, out.length);
    out.push({ model: item.publicName, displayName: itemDisplayName(item), provider, invokable });
  } else if (invokable) {
    out[seen] = { ...out[seen]!, invokable: true };
  }
}

/** The catalog's providers, Anthropic and OpenAI first. */
export function catalogProviders(catalog: readonly CatalogModel[]): string[] {
  return [...new Set(catalog.map((m) => m.provider))].sort(compareProviders);
}

/** A model's name for a person: the catalog's, else one cut from the family name (never the bare id). */
export function nameOf(catalog: readonly CatalogModel[], model: string): string {
  return catalog.find((m) => m.model === model)?.displayName ?? itemDisplayName({ bedrockId: model, publicName: model });
}

// ---------------------------------------------------------------------------------------- the rule

/** The document's global list; a document saved before ADR-103 has none, which allows everything. */
export function globalOf(effective: Json | undefined): GlobalPolicy | null {
  const raw = getPath(effective, ALLOWED_MODELS_PATH);
  if (!Array.isArray(raw)) return null;
  return raw.filter(isObject).map((entry) => ({
    provider: typeof entry["provider"] === "string" ? entry["provider"] : "",
    enabled: entry["enabled"] !== false,
    mode: entry["mode"] === "allow_list" ? "allow_list" : "follow_aws",
    allowed: Array.isArray(entry["allowed"]) ? (entry["allowed"] as string[]) : [],
  }));
}

/** The provider's own entries, else the `*` ones. */
function rulesFor(global: GlobalPolicy, provider: string): ProviderPolicy[] {
  const own = global.filter((rule) => provider !== "" && provider !== ALL_PROVIDERS && rule.provider === provider);
  return own.length > 0 ? own : global.filter((rule) => rule.provider === ALL_PROVIDERS);
}

/** ADR-103's global step, failing closed as the API does: no entry allows nothing, and every one of a provider's entries must allow. */
export function globalAllows(global: GlobalPolicy | null, provider: string, model: string): boolean {
  if (global === null) return true;
  const rules = rulesFor(global, provider);
  return rules.length > 0 && rules.every((rule) => rule.enabled && (rule.mode === "follow_aws" || rule.allowed.includes(model)));
}

/** What the harness's own policy serves, before the global step. */
export function harnessAllows(policy: HarnessPolicy, provider: string, model: string): boolean {
  if (!providersCover(policy.providers, provider)) return false;
  if (policy.mode === "follow_aws" && policy.include_new_models) return true;
  return policy.allowed.includes(model);
}

/**
 * What an agent is offered under a document (the API's `OfferedModels`): every catalog model the
 * global list and the agent's policy allow, invokable or not, its default first, then the
 * catalog's order. The server's `effectiveModels` is the same list; this one answers for a draft.
 */
export function offeredModels(effective: Json | undefined, harness: Harness, catalog: readonly CatalogModel[]): CatalogModel[] {
  const global = globalOf(effective);
  const policy = policyOf(effective, harness);
  const out = catalog.filter((m) => globalAllows(global, m.provider, m.model) && harnessAllows(policy, m.provider, m.model));
  return [...out.filter((m) => m.model === policy.default), ...out.filter((m) => m.model !== policy.default)];
}

// ------------------------------------------------------------------------------------- the rows

export interface ProviderRow {
  provider: string;
  label: string;
  enabled: boolean;
  mode: ProviderPolicy["mode"];
  /** The provider's catalog models the global list allows. */
  allowedCount: number;
  /** The provider's catalog models. */
  total: number;
  /** Of those, how many some installation can call. */
  invokable: number;
  /** "2 of 3 models allowed", "Off", "Every model AWS offers (3)". */
  summary: string;
  /** The provider has no entry of its own and follows the `*` entry. */
  inherited: boolean;
}

/** One row per catalog provider (and per provider the list names that the catalog lacks), `*` last. */
export function providerRows(effective: Json | undefined, catalog: readonly CatalogModel[]): ProviderRow[] {
  const global = globalOf(effective) ?? [];
  const named = global.map((rule) => rule.provider).filter((p) => p !== ALL_PROVIDERS && p !== "");
  const providers = [...new Set([...catalogProviders(catalog), ...named])].sort(compareProviders);
  return providers.map((provider) => providerRow(global, provider, catalog.filter((m) => m.provider === provider)));
}

function providerRow(global: GlobalPolicy, provider: string, models: readonly CatalogModel[]): ProviderRow {
  const rule = entryFor(global, provider);
  const allowedCount = models.filter((m) => globalAllows(global, provider, m.model)).length;
  const total = models.length;
  return {
    provider,
    label: providerLabel(provider),
    enabled: rule.enabled,
    mode: rule.mode,
    allowedCount,
    total,
    invokable: models.filter((m) => m.invokable).length,
    summary: rowSummary(rule, allowedCount, total),
    inherited: !global.some((r) => r.provider === provider),
  };
}

function rowSummary(rule: ProviderPolicy, allowed: number, total: number): string {
  if (!rule.enabled) return "Off: no agent is offered these models";
  if (rule.mode === "follow_aws") return `Every model AWS offers (${total})`;
  return `${allowed} of ${total} ${total === 1 ? "model" : "models"} allowed`;
}

/** The provider's entry as the modal edits it: its own, else a copy of `*`'s, else off. */
export function entryFor(global: GlobalPolicy | null, provider: string): ProviderPolicy {
  const list = global ?? [];
  const own = list.find((r) => r.provider === provider);
  if (own) return { ...own, allowed: [...own.allowed] };
  if (global === null) return { provider, enabled: true, mode: "follow_aws", allowed: [] };
  const wildcard = list.find((r) => r.provider === ALL_PROVIDERS);
  return { provider, enabled: wildcard?.enabled ?? false, mode: wildcard?.mode ?? "follow_aws", allowed: wildcard ? [...wildcard.allowed] : [] };
}

// ------------------------------------------------------------------------------------- an edit

/** What the modal (or the row's toggle) asks for one provider. */
export interface ProviderEdit {
  enabled: boolean;
  mode: ProviderPolicy["mode"];
  /** The ticked family names; ignored under `follow_aws`. */
  allowed: readonly string[];
  /** Per agent, the default the admin re-pointed it to in the same change. */
  repoint?: Readonly<Partial<Record<Harness["key"], string>>>;
}

export interface AffectedDefault {
  harness: Harness;
  /** The default the change takes away. */
  model: string;
  displayName: string;
  /** Where the default can go: the agent's offered models under the edited list. */
  options: CatalogModel[];
  /** The admin's re-point, when one is chosen. */
  repointedTo: string | null;
}

export interface ProviderEditResult {
  /** The document with the provider's entry (and any re-pointed defaults) written. */
  document: JsonObject;
  /** The agents whose default the edit takes away, each with where it can go. */
  affectedDefaults: AffectedDefault[];
  /** Every affected default is re-pointed: the write passes REQ-210. */
  ready: boolean;
}

/**
 * The document with one provider's global entry replaced (pure), and the agents whose default the
 * change would disable (`default_would_be_disabled`): a default allowed before and not after. A
 * re-point is written to that agent's `default`, as the API accepts in the same body. An
 * `allow_list` keeps only names of this provider, in the catalog's order; a name the catalog lacks
 * but the entry held is kept so a model a quiet installation reported is not dropped by accident.
 * A provider entry that would match `*` exactly is still written: the admin chose it.
 */
export function applyProviderEdit(draft: JsonObject, effective: Json | undefined, provider: string, edit: ProviderEdit, catalog: readonly CatalogModel[]): ProviderEditResult {
  const before = globalOf(effective);
  const entry: ProviderPolicy = { provider, enabled: edit.enabled, mode: edit.mode, allowed: edit.mode === "allow_list" ? orderedAllowed(catalog, provider, edit.allowed) : [] };
  const list = before ?? defaultGlobal(catalog);
  const next = list.some((r) => r.provider === provider) ? list.map((r) => (r.provider === provider ? entry : r)) : [...list, entry];
  let document = setPath(draft, ALLOWED_MODELS_PATH, next as unknown as Json);
  const afterEffective = setPath(isObject(effective) ? effective : {}, ALLOWED_MODELS_PATH, next as unknown as Json);
  const affectedDefaults = HARNESSES.flatMap((harness) => affected(harness, effective, afterEffective, catalog, edit.repoint?.[harness.key]));
  for (const hit of affectedDefaults) {
    if (hit.repointedTo !== null) document = setPath(document, `models.${hit.harness.key}.default`, hit.repointedTo);
  }
  return { document, affectedDefaults, ready: affectedDefaults.every((hit) => hit.repointedTo !== null) };
}

function orderedAllowed(catalog: readonly CatalogModel[], provider: string, allowed: readonly string[]): string[] {
  const mine = catalog.filter((m) => m.provider === provider).map((m) => m.model);
  return [...mine.filter((m) => allowed.includes(m)), ...allowed.filter((m) => !mine.includes(m))];
}

/** A list that allows what a document without one allowed: every catalog provider following AWS. */
function defaultGlobal(catalog: readonly CatalogModel[]): ProviderPolicy[] {
  return [...catalogProviders(catalog), ALL_PROVIDERS].map((provider) => ({ provider, enabled: true, mode: "follow_aws", allowed: [] }));
}

function affected(harness: Harness, before: Json | undefined, after: JsonObject, catalog: readonly CatalogModel[], repoint: string | undefined): AffectedDefault[] {
  const current = policyOf(before, harness).default;
  if (current === "") return [];
  const allowedBefore = offeredModels(before, harness, catalog).some((m) => m.model === current);
  const options = offeredModels(after, harness, catalog);
  if (!allowedBefore || options.some((m) => m.model === current)) return [];
  const valid = repoint !== undefined && options.some((m) => m.model === repoint);
  return [{ harness, model: current, displayName: nameOf(catalog, current), options, repointedTo: valid ? repoint : null }];
}

/** The modal's footer, one line per agent: "Claude Code's default (Opus 5.5) stays allowed." */
export function defaultsFooter(effective: Json | undefined, result: ProviderEditResult, catalog: readonly CatalogModel[]): string[] {
  return HARNESSES.map((harness) => {
    const hit = result.affectedDefaults.find((a) => a.harness.key === harness.key);
    const current = policyOf(effective, harness).default;
    if (!hit) return `${harness.label}'s default (${nameOf(catalog, current)}) stays allowed.`;
    if (hit.repointedTo === null) return `${harness.label}'s default (${hit.displayName}) would be disabled. Re-point it to another model.`;
    return `${harness.label}'s default moves from ${hit.displayName} to ${nameOf(catalog, hit.repointedTo)}.`;
  });
}
